import React, { useState } from 'react';
import { Plus, Briefcase, PieChart, Trash2, Sparkles, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card } from './ui/card';
import { mockBrokers } from '../mock';
import DescriptionEditorModal from './DescriptionEditorModal';
import type { CreatePortfolioModalProps, Category } from '../types';
import { CategoryTimeframe, Broker } from '../types';

// Default descriptions for Core and Satellite categories
const DEFAULT_CORE_DESCRIPTION = `1. Objective

The Core Portfolio is designed to:

Capture long-term growth drivers

Own high-quality compounders for multiple years

Let winners run while tightly controlling downside risk

Focus is wealth compounding, not short-term momentum.

2. Eligible Universe
2.1 Sector Constraint (Hard Filter)

Core positions must belong only to the following sectors:

Chemicals / Pharma / Hospitals / Diagnostics

Consumption

Financials

Industrials

Technology

Any stock outside these sectors is not eligible for the Core Portfolio.

2.2 Business Quality Criteria

Stock must qualify as a long-term compounder

Characteristics expected:

Durable business model

Multi-year growth visibility

Ability to reinvest capital at high returns

Holding period mindset: several years

2.3 Chart Structure Requirement

Long-term chart must exhibit:

A clean uptrend

Approximate 45-degree price trajectory

Avoid:

Erratic, vertical, or highly volatile structures

Cyclical or mean-reverting charts

3. Position Sizing Rules
3.1 Initial Entry (First Tranche)

First tranche size:

1% to 2% of total portfolio

Entry must have:

Clear structural stop-loss

Long-term trend confirmation

3.2 Maximum Exposure

Maximum allocation per stock (portfolio-wide): 8%

Includes all tranches combined

No exceptions.

4. Risk Management Framework
4.1 First Tranche Risk

First tranche stop-loss:

Maximum 10%

Stop-loss must be defined at entry

Risk is allowed early, but must be intentional

4.2 Pyramiding Rule

After first tranche:

Only pyramiding is allowed

No averaging down

Additional tranches are added:

Only into strength

Only if long-term structure remains intact

4.3 Risk Rule Beyond 3% Position Size

Once cumulative position size exceeds 3%:

Stop-loss must be adjusted such that:

Position is at breakeven, or

Has negative residual risk

No tranche is allowed if:

Net downside risk increases beyond this point

5. Stop-Loss Logic

There is one active stop-loss per position

The latest tranche's stop-loss applies to the entire position

Stop-loss:

Can only move upward

Must align with long-term structure

This ensures:

Risk compression

Protection of accumulated gains

6. Pyramiding Constraints

Pyramiding must:

Improve overall risk profile

Strengthen conviction via price confirmation

Adding without stop tightening is invalid

Position size growth must be accompanied by risk reduction

7. Holding & Exit Philosophy

Keep riding the winners

Partial profit-taking is discouraged unless:

Long-term structure is damaged

Business thesis breaks

Full exit occurs if:

Stop-loss is hit

Long-term trend is decisively broken

8. Core Principles (Non-Negotiable)

Time in trend > timing the trend

Structure over short-term noise

Compounding requires patience

Protect capital first, returns follow

9. Execution Checklist (Before Every Tranche)

Confirm ALL:

✅ Stock belongs to approved sectors

✅ Business qualifies as a long-term compounder

✅ Chart shows clean 45-degree trend

✅ Entry is into strength

✅ Latest stop-loss tightens or maintains risk

✅ Total position ≤ 8%

✅ Net risk ≤ 0 if position size > 3%`;

const DEFAULT_SATELLITE_DESCRIPTION = `1. Objective

The Satellite Portfolio exists to:

Capture emerging and momentum-driven sectors

Participate early in sectoral moves

Build positions via structured pyramiding

Focus is trend exploitation and alpha, not defensiveness.

2. Eligibility Criteria
2.1 Sector Condition (Gatekeeper)

The sector must be moving

Relative strength vs market is improving

Sector index or majority of stocks show upward momentum

A formal sector breakout is NOT mandatory

Early participation is allowed once sector momentum is evident

2.2 Stock Condition
Acceptable technical setups:
Cup & Handle
Breakout patterns
Tight consolidation near highs
Price action must show:
Expansion
Clean structure
Clear invalidation level

3. Position Sizing Rules
3.1 Maximum Exposure
Maximum position size per stock: 8% of total portfolio
This limit includes all tranches combined
No overrides.

3.2 Tranching & Pyramiding Framework
All positions are built only via pyramiding
No lump-sum entries.
Tranche Addition Rules
Tranches are evaluated weekly
A new tranche can be added only if:
The current week's candle is strong
Price is making or holding higher highs
Trend structure remains intact
Adding into weakness is not allowed

4. Risk Management Framework
4.1 First Tranche Risk
First tranche:
Maximum stop-loss: 10%
Risk must be predefined at entry
Entry is allowed once:
Sector is moving
Stock structure is valid

4.2 Risk Handling Until 2.5% Position Size
For cumulative position size up to 2.5%:
All tranches share the same residual risk pool
Total risk must not exceed initial planned risk
Risk is allowed to stay positive in this phase

4.3 Risk Rule Beyond 2.5% Position Size
Once position size exceeds 2.5%:
The position must be either:
At breakeven, or
Have negative residual risk
No new tranche is allowed if:
Adding it increases net downside risk

5. Stop-Loss Logic (Critical Rule)
There is only ONE active stop-loss per position
The latest tranche's stop-loss becomes the stop-loss for ALL earlier tranches
Stop-loss always:
Moves forward, never backward
Reflects the most recent structural invalidation

This enforces:
Risk compression
True pyramiding discipline

6. Pyramiding Constraints
Every added tranche must:
Improve or maintain risk efficiency
Be added only into strength
Pyramiding without stop tightening is invalid
Risk must reduce or remain flat as size increases

7. Exit & Freeze Conditions

No further tranches allowed if:
Sector momentum weakens materially
Stock violates structure
Weekly strength disappears
Stop-loss breach → full position exit

8. Core Principles (Non-Negotiable)
Momentum over leadership
Structure over stories
Risk compression over position size
Pyramiding is a privilege, not a right

9. Execution Criteria
✅ Sector is moving
✅ Stock structure is intact
✅ Weekly candle is strong
✅ Price is near or at highs
✅ Latest stop-loss tightens or maintains risk
✅ Total position ≤ 8%
✅ Net risk is non-positive if size > 2.5%`;

const getDefaultCategories = (): Category[] => [
  { name: 'Core', percentage: 70, timeframe: CategoryTimeframe.MONTHLY, color: '#4169E1', description: DEFAULT_CORE_DESCRIPTION },
  { name: 'Satellite', percentage: 30, timeframe: CategoryTimeframe.WEEKLY, color: '#10B981', description: DEFAULT_SATELLITE_DESCRIPTION }
];

const CreatePortfolioModal: React.FC<CreatePortfolioModalProps> = ({ isOpen, onClose, onCreate }) => {
  const [portfolioName, setPortfolioName] = useState<string>('');
  const [portfolioSize, setPortfolioSize] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>(getDefaultCategories());
  const [selectedBrokers, setSelectedBrokers] = useState<Broker[]>([]);
  const [editingDescriptionIndex, setEditingDescriptionIndex] = useState<number | null>(null);

  const handleAddCategory = () => {
    setCategories([
      ...categories,
      { name: 'New Category', percentage: 0, timeframe: CategoryTimeframe.MONTHLY, color: '#4169E1', description: '' }
    ]);
  };

  const handleUpdateCategory = (index: number, field: keyof Category, value: string | number) => {
    const newCategories = [...categories];
    if (field === 'percentage') {
      newCategories[index][field] = Number(value);
    } else if (field === 'timeframe') {
      newCategories[index][field] = value as CategoryTimeframe;
    } else {
      newCategories[index][field] = value as string;
    }
    setCategories(newCategories);
  };

  const handleDeleteCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const toggleBroker = (brokerId: Broker) => {
    if (selectedBrokers.includes(brokerId)) {
      setSelectedBrokers(selectedBrokers.filter(id => id !== brokerId));
    } else {
      setSelectedBrokers([...selectedBrokers, brokerId]);
    }
  };

  const totalAllocation = categories.reduce((sum, cat) => sum + (cat.percentage || 0), 0);
  const allCategoriesHaveDescription = categories.every(cat => cat.description && cat.description.trim().length > 0);
  const isValid = totalAllocation === 100 && portfolioName && portfolioSize && selectedBrokers.length > 0 && allCategoriesHaveDescription;

  const handleCreate = () => {
    if (!isValid) {
      return;
    }

    onCreate({
      name: portfolioName,
      totalSize: parseInt(portfolioSize),
      categories: categories,
      brokers: selectedBrokers
    });

    // Reset form
    setPortfolioName('');
    setPortfolioSize('');
    setCategories(getDefaultCategories());
    setSelectedBrokers([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div>
            <DialogTitle className="text-2xl">Create New Portfolio</DialogTitle>
            <p className="text-sm text-gray-500 mt-1">Set up your portfolio with categories and broker connections</p>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
          {/* Left Column - Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Portfolio Information */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold">Portfolio Information</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="portfolio-name">Portfolio Name</Label>
                  <Input
                    id="portfolio-name"
                    value={portfolioName}
                    onChange={(e) => setPortfolioName(e.target.value)}
                    placeholder="e.g., Growth Portfolio"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="portfolio-size">Total Portfolio Size (₹)</Label>
                  <Input
                    id="portfolio-size"
                    type="number"
                    value={portfolioSize}
                    onChange={(e) => setPortfolioSize(e.target.value)}
                    placeholder="e.g., 1000000"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="mb-2 block">
                    Link Brokers <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">Select at least one broker</p>
                  <div className="flex flex-wrap gap-2">
                    {mockBrokers.map((broker) => (
                      <button
                        key={broker.id}
                        onClick={() => toggleBroker(broker.id)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          selectedBrokers.includes(broker.id)
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-semibold mr-2">{broker.logo}</span>
                        {broker.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Category Allocation */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold">Category Allocation</h3>
                </div>
                <span className={`text-sm font-medium ${
                  totalAllocation === 100 ? 'text-green-600' : 'text-red-600'
                }`}>
                  Total: {totalAllocation}%
                </span>
              </div>

              <div className="space-y-4">
                {categories.map((category, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Input
                        type="color"
                        value={category.color}
                        onChange={(e) => handleUpdateCategory(index, 'color', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={category.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateCategory(index, 'name', e.target.value)}
                        className="flex-1"
                        placeholder="Category name"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCategory(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <Label className="text-xs">Allocation %</Label>
                        <Input
                          type="number"
                          value={category.percentage}
                          onChange={(e) => handleUpdateCategory(index, 'percentage', e.target.value)}
                          className="mt-1"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Timeframe</Label>
                        <Select
                          value={category.timeframe}
                          onValueChange={(value) => handleUpdateCategory(index, 'timeframe', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={CategoryTimeframe.DAILY}>Daily</SelectItem>
                            <SelectItem value={CategoryTimeframe.WEEKLY}>Weekly</SelectItem>
                            <SelectItem value={CategoryTimeframe.BI_WEEKLY}>Bi-weekly</SelectItem>
                            <SelectItem value={CategoryTimeframe.MONTHLY}>Monthly</SelectItem>
                            <SelectItem value={CategoryTimeframe.QUARTERLY}>Quarterly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Category Description - Compact with Expand Button */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-3 h-3 text-purple-500" />
                          <Label className="text-xs font-medium">Category Description</Label>
                          <span className="text-red-500 text-xs">*</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1 text-purple-600 border-purple-300 hover:bg-purple-50"
                          onClick={() => setEditingDescriptionIndex(index)}
                        >
                          <Expand className="w-3 h-3" />
                          {category.description ? 'Edit' : 'Add Description'}
                        </Button>
                      </div>
                      
                      {category.description ? (
                        <div className="text-sm text-gray-600 line-clamp-2 bg-white p-2 rounded border">
                          {category.description}
                        </div>
                      ) : (
                        <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                          ⚠️ Description required for AI portfolio review
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-400 text-right mt-1">
                        {(category.description || '').length}/10,000 chars
                      </p>
                    </div>
                  </div>
                ))}

                <Button
                  variant="outline"
                  className="w-full border-dashed border-2"
                  onClick={handleAddCategory}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </div>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Allocation Preview</h3>
              
              {/* Pie Chart */}
              <div className="flex items-center justify-center mb-6">
                <svg width="200" height="200" viewBox="0 0 200 200">
                  {categories.map((category, index) => {
                    const totalPercentage = categories.slice(0, index).reduce((acc, cat) => acc + (cat.percentage || 0), 0);
                    const startAngle = (totalPercentage / 100) * 360;
                    const endAngle = startAngle + ((category.percentage || 0) / 100) * 360;
                    const largeArcFlag = (category.percentage || 0) > 50 ? 1 : 0;

                    const startX = 100 + 90 * Math.cos((Math.PI * startAngle) / 180);
                    const startY = 100 + 90 * Math.sin((Math.PI * startAngle) / 180);
                    const endX = 100 + 90 * Math.cos((Math.PI * endAngle) / 180);
                    const endY = 100 + 90 * Math.sin((Math.PI * endAngle) / 180);

                    return (
                      <path
                        key={category.name + index}
                        d={`M 100 100 L ${startX} ${startY} A 90 90 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                        fill={category.color}
                        opacity="0.9"
                      />
                    );
                  })}
                </svg>
              </div>

              {/* Category List */}
              <div className="space-y-3">
                {categories.map((category, index) => (
                  <div key={category.name + index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{category.percentage}%</p>
                      <p className="text-xs text-gray-500">
                        {portfolioSize ? `₹${((parseInt(portfolioSize) * category.percentage) / 100).toLocaleString('en-IN')}` : '-'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Validation Message */}
            {totalAllocation !== 100 && (
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 text-lg">⚠️</span>
                  <div>
                    <p className="text-sm font-medium text-red-800">Allocation Error</p>
                    <p className="text-xs text-red-700 mt-1">
                      Category allocations must total exactly 100%. Current: {totalAllocation}%
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {!portfolioName && (
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 text-lg">💡</span>
                  <div>
                    <p className="text-sm font-medium text-blue-800">Getting Started</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Enter portfolio name, size, and select at least one broker to begin.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {selectedBrokers.length === 0 && portfolioName && (
              <Card className="p-4 bg-orange-50 border-orange-200">
                <div className="flex items-start gap-2">
                  <span className="text-orange-600 text-lg">⚠️</span>
                  <div>
                    <p className="text-sm font-medium text-orange-800">Broker Required</p>
                    <p className="text-xs text-orange-700 mt-1">
                      Please select at least one broker to continue.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {!allCategoriesHaveDescription && totalAllocation === 100 && portfolioName && selectedBrokers.length > 0 && (
              <Card className="p-4 bg-purple-50 border-purple-200">
                <div className="flex items-start gap-2">
                  <span className="text-purple-600 text-lg">✍️</span>
                  <div>
                    <p className="text-sm font-medium text-purple-800">Category Descriptions Required</p>
                    <p className="text-xs text-purple-700 mt-1">
                      Each category needs a description for AI portfolio review.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 mt-6 pt-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            onClick={handleCreate}
            disabled={!isValid}
          >
            <Plus className="w-4 h-4" />
            Create Portfolio
          </Button>
        </div>
      </DialogContent>

      {/* Description Editor Modal */}
      {editingDescriptionIndex !== null && (
        <DescriptionEditorModal
          isOpen={editingDescriptionIndex !== null}
          onClose={() => setEditingDescriptionIndex(null)}
          onSave={(description) => {
            handleUpdateCategory(editingDescriptionIndex, 'description', description);
            setEditingDescriptionIndex(null);
          }}
          categoryName={categories[editingDescriptionIndex]?.name || ''}
          categoryColor={categories[editingDescriptionIndex]?.color || '#4169E1'}
          initialDescription={categories[editingDescriptionIndex]?.description || ''}
        />
      )}
    </Dialog>
  );
};

export default CreatePortfolioModal;
